import { lazy, Suspense, useEffect } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AppLayout } from "@/layout/AppLayout";
import { RequireAuth, useSession } from "@/features/auth";
import { tokens } from "@/shared/api/http";

// Code-splitting por view: cada feature vira um chunk próprio. Todos são baixados logo ao abrir
// (precarregar), para a troca de tela não mostrar nada antes do skeleton da própria tela.
const telas = {
  Painel: () => import("@/features/dashboard/views"),
  Estoque: () => import("@/features/estoque/views"),
  Clientes: () => import("@/features/clientes/views"),
  Simulador: () => import("@/features/simulador/views"),
  Vendas: () => import("@/features/vendas/views"),
  Compras: () => import("@/features/compras/views"),
  Analises: () => import("@/features/analises/views"),
  Usuarios: () => import("@/features/usuarios/views"),
  Parceiros: () => import("@/features/parceiros/views"),
  Reservas: () => import("@/features/reservas/views"),
  Configuracoes: () => import("@/features/configuracoes/views"),
  Login: () => import("@/features/auth/views"),
  EsqueciSenha: () => import("@/features/auth/views/EsqueciSenha"),
  RedefinirSenha: () => import("@/features/auth/views/RedefinirSenha"),
};
const Painel = lazy(telas.Painel);
const Estoque = lazy(telas.Estoque);
const Clientes = lazy(telas.Clientes);
const Simulador = lazy(telas.Simulador);
const Vendas = lazy(telas.Vendas);
const Compras = lazy(telas.Compras);
const Analises = lazy(telas.Analises);
const Usuarios = lazy(telas.Usuarios);
const Parceiros = lazy(telas.Parceiros);
const Reservas = lazy(telas.Reservas);
const Configuracoes = lazy(telas.Configuracoes);
const Login = lazy(telas.Login);
const EsqueciSenha = lazy(telas.EsqueciSenha);
const RedefinirSenha = lazy(telas.RedefinirSenha);

// Chunk ainda baixando (raro, com o precarregamento): área de conteúdo vazia. Quem mostra
// skeleton é a própria tela, com a forma dela; um skeleton genérico antes só piscaria.
const Loading = () => null;

/**
 * Raiz do site: sem sessão, o login; com sessão (ou login salvo ainda sendo conferido), a tela
 * inicial do sistema, que abre na hora com o skeleton dela.
 */
function Raiz() {
  const { usuario, carregando } = useSession();
  if (usuario || (carregando && tokens.get())) return <Navigate to="/estoque" replace />;
  return (
    <Suspense fallback={null}>
      <Login />
    </Suspense>
  );
}

export function AppRoutes() {
  useEffect(() => {
    for (const carregar of Object.values(telas)) void carregar();
  }, []);

  return (
    <Routes>
      <Route path="/" element={<Raiz />} />
      {/* Públicas: sem sessão. */}
      <Route path="/esqueci-senha" element={<Suspense fallback={null}><EsqueciSenha /></Suspense>} />
      <Route path="/redefinir-senha" element={<Suspense fallback={null}><RedefinirSenha /></Suspense>} />
      {/* Endereço antigo do login. */}
      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        {/* V1: o painel ainda é mock da POC; a entrada é o estoque (vinda da raiz). O painel segue em /painel. */}
        <Route
          path="/painel"
          element={
            <Suspense fallback={<Loading />}>
              <Painel />
            </Suspense>
          }
        />
        <Route
          path="/parceiros"
          element={
            <Suspense fallback={<Loading />}>
              <Parceiros />
            </Suspense>
          }
        />
        <Route
          path="/reservas"
          element={
            <Suspense fallback={<Loading />}>
              <Reservas />
            </Suspense>
          }
        />
        <Route
          path="/configuracoes"
          element={
            <Suspense fallback={<Loading />}>
              <Configuracoes />
            </Suspense>
          }
        />
        <Route
          path="/usuarios"
          element={
            <Suspense fallback={<Loading />}>
              <Usuarios />
            </Suspense>
          }
        />
        <Route
          path="/estoque"
          element={
            <Suspense fallback={<Loading />}>
              <Estoque />
            </Suspense>
          }
        />
        <Route
          path="/clientes"
          element={
            <Suspense fallback={<Loading />}>
              <Clientes />
            </Suspense>
          }
        />
        <Route
          path="/simulador"
          element={
            <Suspense fallback={<Loading />}>
              <Simulador />
            </Suspense>
          }
        />
        <Route
          path="/vendas"
          element={
            <Suspense fallback={<Loading />}>
              <Vendas />
            </Suspense>
          }
        />
        <Route
          path="/compras"
          element={
            <Suspense fallback={<Loading />}>
              <Compras />
            </Suspense>
          }
        />
        <Route
          path="/analises"
          element={
            <Suspense fallback={<Loading />}>
              <Analises />
            </Suspense>
          }
        />
        <Route path="*" element={<Navigate to="/estoque" replace />} />
      </Route>
    </Routes>
  );
}
